import { useState, useEffect } from "react";
import { Code, Award, AlertTriangle, CheckCircle, XCircle, Lightbulb, Send } from "lucide-react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import axios from "axios";
import GameConnector from "./utils/GameConnector";
import { Modal, Button } from 'react-bootstrap'; // Import Modal and Button

// Backend API configuration
const API_BASE_URL = 'https://adaptivebackend.onrender.com/api';

// Define types for the API responses
interface Question {
  id: number;
  difficulty: string;
  text: string;
  hints?: string[];
}

interface CheckResult {
  correct: boolean;
  consecutive_correct: number;
  consecutive_wrong: number;
  next_difficulty: string;
  auto_next?: boolean; // Add new field for auto progression
  stats: {
    current_difficulty: string;
    consecutive_correct: number;
    consecutive_wrong: number;
    questions_used: number;
  };
}

interface CodeLine {
  number: number;
  code: string;
  hasError: boolean;
}

interface ParsedQuestion {
  title: string;
  lines: CodeLine[];
  expectedOutput?: string;
  actualOutput?: string;
}

const DebugGame = () => {
  const [userInput, setUserInput] = useState("");
  const [showHint, setShowHint] = useState(false); // Keep for level tracking logic maybe? Or refactor later.
  const [showHintModal, setShowHintModal] = useState(false); // State for modal visibility
  const [hintLevel, setHintLevel] = useState(0);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [stats, setStats] = useState({
    currentDifficulty: "Easy",
    consecutiveCorrect: 0,
    consecutiveWrong: 0,
    questionsUsed: 0
  });
  const [gameConnected, setGameConnected] = useState(false);
  const connector = GameConnector.getInstance();

  useEffect(() => {
    initializeSystem();

    connector.on('gameSceneReady', () => {
      console.log("Game scene is ready and connected");
      setGameConnected(true);
    });

    connector.on('playerMovementComplete', () => {
      console.log("Player movement animation completed");
      setTimeout(() => {
        fetchNextQuestion();
      }, 500);
    });

    window.addEventListener('beforeunload', syncStatsBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', syncStatsBeforeUnload);
      connector.off('gameSceneReady', () => {});
      connector.off('playerMovementComplete', () => {});
    };
  }, []);

  const syncStatsBeforeUnload = () => {
    if (stats) {
      localStorage.setItem('debugGameStats', JSON.stringify(stats));
    }
  };

  const initializeSystem = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/init`);
      console.log("Initialization response:", response.data);
      setInitialized(true);

      const savedStats = localStorage.getItem('debugGameStats');
      if (savedStats) {
        try {
          setStats(JSON.parse(savedStats));
        } catch (e) {
          console.error("Error parsing saved stats:", e);
        }
      }

      try {
        const statsResponse = await axios.get(`${API_BASE_URL}/stats`);
        if (statsResponse.data) {
          setStats({
            currentDifficulty: statsResponse.data.current_difficulty,
            consecutiveCorrect: statsResponse.data.consecutive_correct,
            consecutiveWrong: statsResponse.data.consecutive_wrong,
            questionsUsed: statsResponse.data.questions_used
          });
        }
      } catch {
        console.log("No existing stats from backend, using default or saved values.");
      }

      fetchNextQuestion();
    } catch (error) {
      console.error("Error initializing the system:", error);
      setResult("Failed to connect to the backend. Please check if the server is running.");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextQuestion = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/question`);
      setCurrentQuestion(response.data);

      setShowHint(false);
      setHintLevel(0);
      setResult("");

      const parsedQuestion = parseQuestionText(response.data.text);
      const fullCode = extractFullCode(parsedQuestion.lines);
      setUserInput(fullCode);
    } catch (error) {
      console.error("Error fetching question:", error);
      setResult("Failed to fetch the next question.");
    } finally {
      setLoading(false);
    }
  };

  const extractFullCode = (lines: CodeLine[]): string => {
    return lines
      .filter(line => {
        const trimmedCode = line.code.trim();
        return trimmedCode.length > 0 &&
               !trimmedCode.startsWith('#') &&
               !trimmedCode.startsWith('"""') &&
               !trimmedCode.startsWith("'''");
      })
      .map(line => line.code)
      .join('\n');
  };

  const checkAnswer = async () => {
    if (!currentQuestion) return;

    try {
      setLoading(true);
      console.log("Submitting answer:", userInput);

      const response = await axios.post(`${API_BASE_URL}/check`, { answer: userInput });
      const data: CheckResult = response.data;

      console.log("Response from backend:", data);

      const newStats = {
        currentDifficulty: data.stats.current_difficulty,
        consecutiveCorrect: data.stats.consecutive_correct,
        consecutiveWrong: data.stats.consecutive_wrong,
        questionsUsed: data.stats.questions_used
      };

      setStats(newStats);
      localStorage.setItem('debugGameStats', JSON.stringify(newStats));

      if (data.correct === true) {
        setResult("Correct! ✨");

        if (gameConnected) {
          connector.emit('puzzleSolved');
        } else {
          setTimeout(() => {
            fetchNextQuestion();
          }, 1500);
        }
      } else {
        // Check if we need to automatically move to the next question (after 3 failed attempts)
        if (data.auto_next === true) {
          setResult("Moving to the next question after 3 failed attempts...");

          if (gameConnected) {
            connector.emit('puzzleFailed');
            // In game mode, wait for animation to complete before fetching next question
            setTimeout(() => {
              fetchNextQuestion();
            }, 2500); // Longer delay to allow for game animation
          } else {
            // Auto-switch to next question
            setTimeout(() => {
              fetchNextQuestion();
            }, 1500);
          }
        } else {
          setResult("Try again!");
          if (gameConnected) {
            connector.emit('puzzleFailed'); // Emit an event for failure
          }
        }
      }
    } catch (error) {
      console.error("Error checking answer:", error);
      setResult("Failed to check the answer.");
    } finally {
      setLoading(false);
    }
  };

  const parseQuestionText = (text: string): ParsedQuestion => {
    try {
      const lines = text.split('\n');
      const codeLines: CodeLine[] = [];
      let expectedOutput = "";
      let actualOutput = "";
      let title = "Debug this code";

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.includes("Expected output:")) {
          expectedOutput = line.split("Expected output:")[1].trim();
        } else if (line.includes("Actual output:")) {
          actualOutput = line.split("Actual output:")[1].trim();
        } else if (line.trim().length > 0 && !title.includes("Debug")) {
          title = line;
        } else {
          codeLines.push({
            number: i + 1,
            code: line,
            hasError: line.includes("# This line has a problem") || line.includes("ERROR")
          });
        }
      }

      return {
        title: title || `${currentQuestion?.difficulty} Challenge`,
        lines: codeLines,
        expectedOutput,
        actualOutput
      };
    } catch (e) {
      console.error("Error parsing question:", e);
      return {
        title: `${currentQuestion?.difficulty} Challenge`,
        lines: [{ number: 1, code: text, hasError: false }]
      };
    }
  };

  const handleHintClick = () => {
    let nextLevel = hintLevel;
    if (!showHint) { // Use showHint to track if *any* hint has been shown yet for level logic
      setShowHint(true);
      nextLevel = 1;
    } else if (hintLevel < 3) {
      nextLevel = hintLevel + 1;
    }
    setHintLevel(nextLevel);
    setShowHintModal(true); // Open the modal

    // Emit the hint used event to the backend
    if (gameConnected) {
      GameConnector.getInstance().emit('hintUsed');
    }
  };

  const handleCloseHintModal = () => setShowHintModal(false);

  const getCurrentHint = () => {
    if (!currentQuestion?.hints || !Array.isArray(currentQuestion.hints) || currentQuestion.hints.length === 0) {
      return "Look carefully at the code and try to identify any syntax or logical errors.";
    }

    const availableHints = currentQuestion.hints;
    const maxLevel = Math.min(hintLevel, availableHints.length);
    return availableHints[maxLevel - 1];
  };

  const formattedQuestion = currentQuestion ? parseQuestionText(currentQuestion.text) : null;

  if (loading && !currentQuestion) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h3>Loading challenge...</h3>
        </div>
      </div>
    );
  }

  if (!initialized || !currentQuestion) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center p-5">
                <AlertTriangle size={48} className="text-warning mb-3" />
                <h2 className="mb-4">Connection Error</h2>
                <p className="lead mb-4">
                  {result || "Failed to initialize the question system. Please check if the backend server is running."}
                </p>
                <button className="btn btn-primary btn-lg px-4" onClick={initializeSystem}>
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <div className="bg-light rounded-3 p-3 mb-4 shadow-sm">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <Code className="text-primary me-2" size={24} />
            <h4 className="m-0">Debug Challenge</h4>
          </div>
          <div>
            <span className="badge bg-primary rounded-pill fs-6 me-2 px-3 py-2">
              Level: {stats.currentDifficulty}
            </span>
            <span className="badge bg-secondary rounded-pill fs-6 me-2 px-3 py-2">
              <Award className="me-1" size={16} />
              Attempted: {stats.questionsUsed}
            </span>

          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow border-0 mb-4">
            <div className="card-header bg-white border-bottom-0 py-3">
              <h5 className="card-title mb-0 fw-bold">{formattedQuestion?.title}</h5>
            </div>
            <div className="card-body">
              <div className="bg-light rounded p-3 mb-4 position-relative" style={{ maxHeight: "400px", overflowY: "auto" }}>
                <div className="font-monospace">
                  {formattedQuestion?.lines.map((line: CodeLine, index: number) => (
                    <div key={index} className="d-flex mb-1">
                      <span className="me-2 text-secondary user-select-none" style={{ width: "30px", textAlign: "right" }}>
                        {line.number}
                      </span>
                      <span
                        className={line.hasError ? "text-danger fw-bold" : ""}
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {line.code}
                        {line.hasError && (
                          <span className="ms-2 badge bg-danger-subtle text-danger">
                            <AlertTriangle size={14} className="me-1" /> Error here
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                {(formattedQuestion?.expectedOutput || formattedQuestion?.actualOutput) && (
                  <div className="mt-4 pt-3 border-top text-secondary">
                    {formattedQuestion?.expectedOutput && (
                      <div className="d-flex align-items-center mb-2">
                        <span className="badge bg-success me-2">Expected</span>
                        <code>{formattedQuestion.expectedOutput}</code>
                      </div>
                    )}
                    {formattedQuestion?.actualOutput && (
                      <div className="d-flex align-items-center">
                        <span className="badge bg-danger me-2">Actual</span>
                        <code>{formattedQuestion.actualOutput}</code>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="form-label fw-bold mb-2">
                  Your Solution:
                </label>
                <div className="border rounded overflow-hidden">
                  <CodeMirror
                    value={userInput}
                    height="180px"
                    extensions={[python()]}
                    onChange={(value) => setUserInput(value)}
                    className="border-0"
                    theme="light"
                  />
                </div>
                <div className="form-text mt-1">
                  Fix the code and submit your complete solution.
                </div>
              </div>

              <div className="d-flex gap-3">
                <button
                  onClick={checkAnswer}
                  className="btn btn-primary d-flex align-items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Checking...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="me-2" />
                      Submit Solution
                    </>
                  )}
                </button>

                <button
                  onClick={handleHintClick}
                  className={`btn ${hintLevel > 0 ? 'btn-warning' : 'btn-outline-warning'} d-flex align-items-center`} // Change condition slightly
                >
                  <Lightbulb size={18} className="me-2" />
                  {hintLevel === 0 ? "Show Hint" : `Hint (Level ${hintLevel}/3)`}
                </button>
              </div>

              {/* Remove the inline hint display block */}
              {/* {showHint && ( ... removed ... )} */}

              {result && (
                <div
                  className={`mt-4 p-3 rounded border d-flex align-items-center ${
                    result.includes("Correct")
                      ? "bg-success-subtle border-success"
                      : "bg-danger-subtle border-danger"
                  }`}
                >
                  {result.includes("Correct") ? (
                    <CheckCircle size={24} className="text-success me-3" />
                  ) : (
                    <XCircle size={24} className="text-danger me-3" />
                  )}
                  <div>
                    <h6 className="mb-0 fw-bold">
                      {result.includes("Correct") ? "Success!" : "Not quite right"}
                    </h6>
                    <p className="mb-0">
                      {result.includes("Correct")
                        ? "Great job! Moving to the next challenge shortly..."
                        : result.includes("Moving to the next question")
                        ? "Moving to the next question after 3 failed attempts..."
                        : "Keep trying! Check your solution carefully."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add the Hint Modal */}
      <Modal show={showHintModal} onHide={handleCloseHintModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Lightbulb size={20} className="text-warning me-2" />
            Hint Level {hintLevel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{getCurrentHint()}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseHintModal}>
            Close
          </Button>
          {hintLevel < 3 && (
             <Button variant="warning" onClick={handleHintClick}>
               Next Hint Level ({hintLevel + 1}/3)
             </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DebugGame;
